/**
 * Play English pronunciation with quality-first fallbacks:
 * 1. Healthy stored MP3 (skip broken dictionaryapi.dev /media — Cloudflare 522)
 * 2. Google Dictionary Oxford / dated human recordings (client-side)
 * 3. Lexio `/api/pronunciation` (server: same gstatic → Translate TTS for phrases)
 * 4. Browser SpeechSynthesis only as last resort
 *
 * Winning source is remembered per word so we do not re-probe dead gstatic URLs
 * on every click (common for words missing from the Oxford/dated CDN).
 *
 * Important: short MP3s often never fire `canplaythrough` in Chrome — use
 * `audio.play()` + `playing` / `error` instead.
 */

import { apiClient } from '@/api/client'

const UNRELIABLE_AUDIO_HOST = /api\.dictionaryapi\.dev\/media/i
const AUDIO_TIMEOUT_MS = 3500
const SOURCE_CACHE_KEY = 'lexio:pronunciation-source:v1'
const SOURCE_CACHE_MAX = 200

/** Single dictionary headword: letters, optional internal hyphen. */
const GSTATIC_WORD = /^[a-z]+(?:-[a-z]+)*$/i

type RememberedSource =
  | { kind: 'url'; url: string }
  | { kind: 'backend' }
  | { kind: 'tts' }

const sourceCache = new Map<string, RememberedSource>()
let storageHydrated = false

function cacheKey(text: string): string {
  return text.trim().toLowerCase()
}

function hydrateSourceCache(): void {
  if (storageHydrated || typeof window === 'undefined') return
  storageHydrated = true
  try {
    const raw = sessionStorage.getItem(SOURCE_CACHE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as Record<string, RememberedSource>
    for (const [key, value] of Object.entries(parsed)) {
      if (!key || !value || typeof value !== 'object' || !('kind' in value)) continue
      if (value.kind === 'url' && typeof value.url === 'string') {
        sourceCache.set(key, value)
      } else if (value.kind === 'backend' || value.kind === 'tts') {
        sourceCache.set(key, value)
      }
    }
  } catch {
    // ignore corrupt storage
  }
}

function persistSourceCache(): void {
  if (typeof window === 'undefined') return
  try {
    const obj: Record<string, RememberedSource> = {}
    for (const [key, value] of sourceCache) {
      obj[key] = value
    }
    sessionStorage.setItem(SOURCE_CACHE_KEY, JSON.stringify(obj))
  } catch {
    // quota / private mode — in-memory still works
  }
}

function rememberSource(text: string, source: RememberedSource): void {
  const key = cacheKey(text)
  if (!key) return
  hydrateSourceCache()
  if (sourceCache.has(key)) sourceCache.delete(key)
  sourceCache.set(key, source)
  while (sourceCache.size > SOURCE_CACHE_MAX) {
    const oldest = sourceCache.keys().next().value
    if (oldest === undefined) break
    sourceCache.delete(oldest)
  }
  persistSourceCache()
}

function forgetSource(text: string): void {
  const key = cacheKey(text)
  if (!key) return
  hydrateSourceCache()
  if (!sourceCache.delete(key)) return
  persistSourceCache()
}

function getRememberedSource(text: string): RememberedSource | undefined {
  const key = cacheKey(text)
  if (!key) return undefined
  hydrateSourceCache()
  return sourceCache.get(key)
}

function isUnreliableAudioUrl(url: string): boolean {
  return UNRELIABLE_AUDIO_HOST.test(url)
}

function gstaticCandidates(word: string): string[] {
  const slug = word.trim().toLowerCase()
  if (!GSTATIC_WORD.test(slug) || slug.length > 40) return []
  // Prefer US Oxford (clear for learners), then GB, then dated mirrors.
  return [
    `https://ssl.gstatic.com/dictionary/static/sounds/oxford/${slug}--_us_1.mp3`,
    `https://ssl.gstatic.com/dictionary/static/sounds/oxford/${slug}--_gb_1.mp3`,
    `https://ssl.gstatic.com/dictionary/static/sounds/20200429/${slug}--_us_1.mp3`,
    `https://ssl.gstatic.com/dictionary/static/sounds/20200429/${slug}--_gb_1.mp3`,
  ]
}

function pickBestVoice(): SpeechSynthesisVoice | null {
  if (typeof window === 'undefined' || !window.speechSynthesis) return null
  const voices = window.speechSynthesis.getVoices()
  if (!voices.length) return null
  const en = voices.filter((v) => v.lang.toLowerCase().startsWith('en'))
  const pool = en.length ? en : voices
  const rank = (v: SpeechSynthesisVoice) => {
    const n = `${v.name} ${v.lang}`.toLowerCase()
    if (n.includes('natural') || n.includes('neural') || n.includes('premium')) return 0
    if (n.includes('google')) return 1
    if (n.includes('microsoft') && (n.includes('aria') || n.includes('guy') || n.includes('jenny')))
      return 2
    if (n.includes('samantha') || n.includes('daniel') || n.includes('karen')) return 3
    if (v.lang.toLowerCase().startsWith('en-us') || v.lang.toLowerCase().startsWith('en-gb'))
      return 4
    return 5
  }
  return [...pool].sort((a, b) => rank(a) - rank(b))[0] ?? null
}

function speakWithTts(text: string): void {
  if (typeof window === 'undefined' || !window.speechSynthesis || !text.trim()) return
  window.speechSynthesis.cancel()
  const utterance = new SpeechSynthesisUtterance(text.trim())
  const voice = pickBestVoice()
  if (voice) {
    utterance.voice = voice
    utterance.lang = voice.lang
  } else {
    utterance.lang = 'en-US'
  }
  utterance.rate = 0.92
  window.speechSynthesis.speak(utterance)
}

function releaseAudioElement(audio: HTMLAudioElement): void {
  audio.pause()
  audio.removeAttribute('src')
  try {
    audio.load()
  } catch {
    // ignore
  }
}

/**
 * Play one URL to completion.
 * Resolves after `ended` (not on `playing`) — resolving early + pause/clear was
 * killing sound immediately (looked like “speaker does nothing”).
 * Start timeout only covers time-to-first-audio; playback may run longer.
 */
function playHtmlAudio(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const audio = new Audio()
    let settled = false
    let started = false
    let endSafetyTimer: number | undefined

    const teardownListeners = () => {
      window.clearTimeout(startTimer)
      if (endSafetyTimer !== undefined) window.clearTimeout(endSafetyTimer)
      audio.removeEventListener('playing', onPlaying)
      audio.removeEventListener('error', onError)
      audio.removeEventListener('ended', onEnded)
    }

    const fail = (err: unknown) => {
      if (settled) return
      settled = true
      teardownListeners()
      releaseAudioElement(audio)
      reject(err instanceof Error ? err : new Error(String(err)))
    }

    const succeed = () => {
      if (settled) return
      settled = true
      teardownListeners()
      releaseAudioElement(audio)
      resolve()
    }

    const onPlaying = () => {
      started = true
      window.clearTimeout(startTimer)
      // Safety if `ended` never fires (rare decode/stream glitch).
      endSafetyTimer = window.setTimeout(succeed, 20_000)
    }
    const onEnded = () => succeed()
    const onError = () => fail(new Error('audio error'))
    const startTimer = window.setTimeout(() => {
      if (!started) fail(new Error('audio timeout'))
    }, AUDIO_TIMEOUT_MS)

    audio.addEventListener('playing', onPlaying, { once: true })
    audio.addEventListener('error', onError, { once: true })
    audio.addEventListener('ended', onEnded, { once: true })
    audio.preload = 'auto'
    audio.src = url
    void audio.play().catch((err) => fail(err))
  })
}

/** @returns winning URL, or null if none played */
async function playCandidateChain(urls: string[]): Promise<string | null> {
  for (const url of urls) {
    try {
      await playHtmlAudio(url)
      return url
    } catch {
      // try next
    }
  }
  return null
}

async function playViaBackend(text: string): Promise<boolean> {
  try {
    const res = await apiClient.get<Blob>('/pronunciation', {
      params: { q: text },
      responseType: 'blob',
      timeout: 10000,
      // Avoid default application/json on a binary endpoint
      headers: { Accept: 'audio/mpeg,audio/*,*/*' },
    })
    if (!(res.data instanceof Blob) || res.data.size < 500) return false
    const type = res.data.type || ''
    if (type.includes('text/html') || type.includes('application/json')) {
      return false
    }
    const objectUrl = URL.createObjectURL(res.data)
    try {
      await playHtmlAudio(objectUrl)
      return true
    } finally {
      URL.revokeObjectURL(objectUrl)
    }
  } catch {
    return false
  }
}

async function playRemembered(
  text: string,
  source: RememberedSource,
): Promise<boolean> {
  if (source.kind === 'url') {
    try {
      await playHtmlAudio(source.url)
      return true
    } catch {
      forgetSource(text)
      return false
    }
  }
  if (source.kind === 'backend') {
    if (await playViaBackend(text)) return true
    forgetSource(text)
    return false
  }
  speakWithTts(text)
  return true
}

/**
 * @param text English surface to speak (word or short phrase)
 * @param audioUrl optional remote MP3 from enrichment
 */
export function playPronunciation(text: string, audioUrl?: string | null): void {
  const trimmed = text.trim()
  if (!trimmed && !audioUrl) return

  void (async () => {
    if (trimmed) {
      const remembered = getRememberedSource(trimmed)
      if (remembered && (await playRemembered(trimmed, remembered))) return
    }

    const direct: string[] = []
    if (audioUrl && !isUnreliableAudioUrl(audioUrl)) {
      direct.push(audioUrl)
    }
    if (trimmed) {
      direct.push(...gstaticCandidates(trimmed))
    }

    // Single headwords: human Oxford first (client), then proxied same source.
    const wonUrl = await playCandidateChain(direct)
    if (wonUrl) {
      if (trimmed) rememberSource(trimmed, { kind: 'url', url: wonUrl })
      return
    }
    if (trimmed && (await playViaBackend(trimmed))) {
      rememberSource(trimmed, { kind: 'backend' })
      return
    }
    if (trimmed) {
      rememberSource(trimmed, { kind: 'tts' })
      speakWithTts(trimmed)
    }
  })()
}
